import { graphql } from "gql";
import request from "graphql-request";
import { BACKEND_DOMAIN } from "utils/constants";
import { buildRequestHeaders } from "utils/helpers";

const mutationUpdateCustomPlanMeasures = (
    accessToken: string,
    data: {
        customPlanId: string;
        measures: CarbonReductionMeasureInput[];
    }
) =>
    request({
        url: `${BACKEND_DOMAIN}/graphql`,
        document: mutationQuery,
        variables: {
            id: data.customPlanId,
            measures: toAPIRequest(data.measures),
        },
        requestHeaders: buildRequestHeaders(accessToken),
    })
        .then((data) => {
            if (data) {
                return data;
            }
            throw new Error("Error updating custom plan measures");
        })
        .catch((error) => {
            console.error("GraphQL Error:", error);
            throw error;
        });

const mutationQuery = graphql(`
    mutation updateCustomPlanMeasures(
        $id: Uuid!
        $measures: [CarbonReductionMeasureInput]!
    ) {
        updateCustomPlanMeasures(id: $id, measures: $measures)
    }
`);

export default mutationUpdateCustomPlanMeasures;

// This method is responsible for converting the CarbonReductionMeasureInput
// interface into a structure that the api can ingest. One of the primary converions
// takes place in the revenueEffect which expects a list of revenues by year while the
// CarbonReductionMeasureInput provides a single value which should be expanded over
// the lifetime of measure.
const toAPIRequest = (measures: CarbonReductionMeasureInput[]) =>
    measures.map((measure) => ({
        year: measure.year!,
        crmId: measure.crmId,
        measureCost: measure.measureCost,
        measureSize: measure.measureSize,
        totalIncentives: measure.totalIncentives,
        category: measure.category,
        description: measure.description,
        lifetime: measure.lifetime,
        name: measure.name,
        sizeUnit: measure.sizeUnit,
        loadReductionEffects: measure.loadReductionEffects,
        revenueEffect: convertRevenueEffectInputToAPIStruct(measure),
        landlordShareCost: measure.landlordShareCost,
        landlordShareElectricity: measure.landlordShareElectricity,
        landlordShareNaturalGas: measure.landlordShareNaturalGas,
        landlordShareSteam: measure.landlordShareSteam,
        likeForLikeCost: measure.likeForLikeCost,
        consumptionReductionEffects: measure.consumptionReductionEffects,
        fuelCostReductionEffects: measure.fuelCostReductionEffects,
        carbonIntensityReductionEffects: measure.carbonIntensityReductionEffects,
        notes: measure.notes,
        implementationStatus: measure.implementationStatus,
    }));

const convertRevenueEffectInputToAPIStruct = (
    measureInput: CarbonReductionMeasureInput
) => {
    if (measureInput.revenueEffect == null) {
        return null;
    }
    const reportingLifetime = 2050 - measureInput.year! + 1;
    return {
        revenue_by_year: Array(reportingLifetime).fill(
        {
          revenue: measureInput.revenueEffect.annualRevenue
        }
        ),
        description: measureInput.revenueEffect.description,
    };
};
